import { gql, useMutation, useQuery } from "@apollo/client";

// ── Types ─────────────────────────────────────

export type TrackerPlacement = "head" | "body_start" | "body_end";

export interface CustomTracker {
  id: string;
  name: string;
  code: string;
  placement: TrackerPlacement;
  enabled: boolean;
  sortOrder: number;
}

export interface AllCustomTrackersData {
  allCustomTrackers: CustomTracker[] | null;
}

// ── GraphQL Documents ─────────────────────────────────────

const TRACKER_FIELDS = `
  id
  name
  code
  placement
  enabled
  sortOrder
`;

export const ALL_CUSTOM_TRACKERS = gql`
  query AllCustomTrackers {
    allCustomTrackers(page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC") {
      ${TRACKER_FIELDS}
    }
  }
`;

export const CREATE_CUSTOM_TRACKER = gql`
  mutation CreateCustomTracker(
    $name: String!
    $code: String!
    $placement: String
    $enabled: Boolean
    $sortOrder: Int
  ) {
    createCustomTracker(
      name: $name
      code: $code
      placement: $placement
      enabled: $enabled
      sortOrder: $sortOrder
    ) {
      ${TRACKER_FIELDS}
    }
  }
`;

export const UPDATE_CUSTOM_TRACKER = gql`
  mutation UpdateCustomTracker(
    $id: String!
    $name: String
    $code: String
    $placement: String
    $enabled: Boolean
  ) {
    updateCustomTracker(
      id: $id
      name: $name
      code: $code
      placement: $placement
      enabled: $enabled
    ) {
      ${TRACKER_FIELDS}
    }
  }
`;

export const DELETE_CUSTOM_TRACKER = gql`
  mutation DeleteCustomTracker($id: String!) {
    deleteCustomTracker(id: $id) {
      id
    }
  }
`;

export const REORDER_CUSTOM_TRACKERS = gql`
  mutation ReorderCustomTrackers($ids: [String!]!) {
    reorderCustomTrackers(ids: $ids) {
      id
      sortOrder
    }
  }
`;

// ── Hooks ─────────────────────────────────────

export function useCustomTrackersQuery() {
  // cache-and-network: rows change from under us via reorder, and a stale
  // order is worse than a flash of the previous one.
  return useQuery<AllCustomTrackersData>(ALL_CUSTOM_TRACKERS, {
    fetchPolicy: "cache-and-network",
  });
}

export function useCreateCustomTrackerMutation() {
  return useMutation(CREATE_CUSTOM_TRACKER);
}

export function useUpdateCustomTrackerMutation() {
  return useMutation(UPDATE_CUSTOM_TRACKER);
}

export function useDeleteCustomTrackerMutation() {
  return useMutation(DELETE_CUSTOM_TRACKER);
}

export function useReorderCustomTrackersMutation() {
  return useMutation(REORDER_CUSTOM_TRACKERS);
}
